import { ClusterCommunityDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as ClusterCommunityActions from './cluster-community.actions';

export const CLUSTER_COMMUNITY_FEATURE_KEY = 'clustercommunity';

export interface ClusterCommunityState
  extends EntityState<ClusterCommunityDto> {
  selectedClusterCommunityId?: string | number; // which ClusterCommunity record has been selected
  loaded: boolean; // has the ClusterCommunity list been loaded
  error?: string | null; // last none error (if any)
}

export interface ClusterCommunityPartialState {
  readonly [CLUSTER_COMMUNITY_FEATURE_KEY]: ClusterCommunityState;
}

export const clusterCommunityAdapter: EntityAdapter<ClusterCommunityDto> = createEntityAdapter<
  ClusterCommunityDto
>({
  selectId: (clusterCommunity) => clusterCommunity.id,
  sortComparer: false,
});

export const initialState: ClusterCommunityState = clusterCommunityAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedClusterCommunityId: null,
  }
);

const clusterCommunityReducer = createReducer(
  initialState,
  on(ClusterCommunityActions.loadClusterCommunity, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ClusterCommunityActions.loadClusterCommunitySuccess,
    (state, { clusterCommunity }) =>
      clusterCommunityAdapter.addAll(clusterCommunity, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ClusterCommunityActions.loadClusterCommunityFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(ClusterCommunityActions.addClusterCommunity, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ClusterCommunityActions.addClusterCommunitySuccess,
    (state, { newClusterCommunity }) =>
      clusterCommunityAdapter.addMany(newClusterCommunity, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ClusterCommunityActions.addClusterCommunityFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(ClusterCommunityActions.updateClusterCommunity, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ClusterCommunityActions.updateClusterCommunitySuccess,
    (state, { updateClusterCommunity }) =>
      clusterCommunityAdapter.upsertMany(updateClusterCommunity, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ClusterCommunityActions.updateClusterCommunityFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(ClusterCommunityActions.removeClusterCommunity, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(ClusterCommunityActions.removeClusterCommunitySuccess, (state, { ids }) =>
    clusterCommunityAdapter.removeMany(ids, { ...state, loaded: false })
  ),
  on(
    ClusterCommunityActions.removeClusterCommunityFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  )
);

export function reducer(
  state: ClusterCommunityState | undefined,
  action: Action
) {
  return clusterCommunityReducer(state, action);
}
